import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { apiFetch } from "../lib/api";
import type { HowToVideosResponse } from "@family/shared";

function Shot({ src, alt }: { src: string; alt: string }) {
  return (
    <img
      src={`/release-notes/${src}`}
      alt={alt}
      loading="lazy"
      style={{ width: "100%", maxWidth: 820, border: "1px solid var(--c-border)", borderRadius: 8 }}
    />
  );
}

/** The family how-to video, in the member's language, phone or computer cut. */
function HowToVideo({ familySlug }: { familySlug: string }) {
  const q = useQuery<HowToVideosResponse>({
    queryKey: ["howto-videos", familySlug],
    queryFn: () => apiFetch<HowToVideosResponse>(`/families/${familySlug}/howto-videos`),
    staleTime: 10 * 60 * 1000,
  });
  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;
  const videos = q.data?.videos ?? [];
  const languages = [...new Set(videos.map((v) => v.language))];
  const [language, setLanguage] = useState<string | null>(null);
  const [form, setForm] = useState<"desktop" | "mobile">(isPhone ? "mobile" : "desktop");
  if (videos.length === 0) return null;
  const lang = language ?? languages[0]!;
  const forLang = videos.filter((v) => v.language === lang);
  const chosen = forLang.find((v) => v.form === form) ?? forLang[0]!;
  const chip = (on: boolean): React.CSSProperties => ({
    padding: "4px 12px",
    borderRadius: "var(--r-pill)",
    border: on ? "1px solid var(--c-accent)" : "1px solid var(--c-border-strong)",
    background: on ? "var(--c-accent)" : "transparent",
    color: on ? "var(--c-accent-contrast)" : "var(--c-text)",
    fontSize: "0.85rem",
    minHeight: 30,
  });
  return (
    <section className="stack">
      <h2>Watch the video first</h2>
      <p className="text-muted" style={{ margin: 0 }}>
        Six minutes, made by the family. Pick your language, and the cut for
        the device you're on.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {languages.map((l) => (
          <button key={l} type="button" style={chip(l === lang)} onClick={() => setLanguage(l)}>
            {videos.find((v) => v.language === l)?.label ?? l}
          </button>
        ))}
        <span className="text-subtle" style={{ margin: "0 4px" }}>·</span>
        {(["mobile", "desktop"] as const).map((f) =>
          forLang.some((v) => v.form === f) ? (
            <button key={f} type="button" style={chip(chosen.form === f)} onClick={() => setForm(f)}>
              {f === "mobile" ? "Phone" : "Computer"}
            </button>
          ) : null,
        )}
      </div>
      <video
        key={chosen.url}
        controls
        playsInline
        preload="metadata"
        src={chosen.url}
        // Branded poster (the recording's first frame is blank). The cuts
        // are 780×1688 (phone) and 1920×1080 (computer); fixing the box to
        // that shape avoids a grey placeholder before metadata arrives.
        poster={familySlug === "adgoy" ? `/howto-poster-${chosen.form}.jpg` : undefined}
        style={{
          width: "100%",
          maxWidth: chosen.form === "mobile" ? 400 : 900,
          aspectRatio: chosen.form === "mobile" ? "780 / 1688" : "16 / 9",
          objectFit: "cover",
          borderRadius: 12,
          background: "#1c2436",
        }}
      />
    </section>
  );
}

/**
 * "How to" — the everyday things, in the same order as the family how-to
 * video. Plain language, no admin material; visible to every member.
 */
export function HowToPage() {
  const meQuery = useMe();
  const membership = resolveMembership(meQuery.data);
  const family = membership?.familyName ?? "the family";

  return (
    <>
      <AppHeader />
      <main className="page stack-lg howto">
        <div>
          <h1>How to use the site</h1>
          <p className="text-muted" style={{ margin: 0 }}>
            The everyday things, in the order most people meet them. This is a
            private site for {family}: no ads, no strangers, and nothing leaves
            the family.
          </p>
        </div>

        {membership && <HowToVideo familySlug={membership.familySlug} />}

        <section className="stack">
          <h2>1. Getting in</h2>
          <ul>
            <li><strong>Google account</strong> is the easiest: one tap, choose your account, done.</li>
            <li><strong>Phone number</strong> also works: type it in, you get a six-digit code by SMS, enter it.</li>
            <li>The first time, an admin approves you. Until then you'll see "you're in the queue". You get an email when you're in.</li>
            <li><strong>On your phone, add the site to your home screen</strong> (Safari: Share → Add to Home Screen). It then opens like an app and can show notifications.</li>
          </ul>
        </section>

        <section className="stack">
          <h2>2. Your profile</h2>
          <ul>
            <li>Add a photo, your name and the <strong>city where you live</strong>. The city is how you appear on the World map.</li>
            <li>Tell us <strong>which branch of the family</strong> you belong to and your parents' names. That's how the admin finds you in the family tree and links your account to your person there.</li>
            <li>You can change all of it later from <Link to="/profile">My profile</Link> in the menu.</li>
          </ul>
          <Shot src="sep5-onboarding.png" alt="The profile page with the branch picker and parent names" />
        </section>

        <section className="stack">
          <h2>3. The home page</h2>
          <ul>
            <li>All our memories, <strong>sorted by year</strong>: old photographs next to something from last week.</li>
            <li><strong>Recently added</strong> at the top shows what's new. Tap a card to jump to it.</li>
            <li>Tap the ❤️ on a post you like, or leave a comment. It makes someone's day.</li>
            <li>The search box finds posts by word or by year. Try typing <em>1979</em>.</li>
          </ul>
          <Shot src="sep5-home.png" alt="The stats bar at the top of the home page" />
        </section>

        <section className="stack">
          <h2>4. Sharing a memory</h2>
          <ul>
            <li>Press <strong>+ New post</strong>, write a few words, add photos or a video.</li>
            <li>If the photo is old, set the <strong>year it was taken</strong> so it lands in the right place on the timeline.</li>
            <li><strong>Share with</strong> decides who sees it: everyone, or just one group (your branch, the cousins). Handy for surprises.</li>
            <li>Add a <strong>location</strong> and the post shows as a pin on the World map.</li>
            <li>Open a photo and <strong>tag the people</strong> in it. Every tagged photo then appears on that person's page in the tree.</li>
          </ul>
          <Shot src="howto-newpost.png" alt="The new post form with Share with and photo upload" />
        </section>

        <section className="stack">
          <h2>5. Events</h2>
          <ul>
            <li>Under <Link to="/events">Events</Link> you find our gatherings, past and coming.</li>
            <li>Say whether you're coming with <strong>RSVP</strong>, and see who else is.</li>
            <li>Each event keeps its own photos and videos, sorted into albums.</li>
          </ul>
        </section>

        <section className="stack">
          <h2>6. Albums</h2>
          <ul>
            <li>Our old paper albums get a second life here: someone scans the pages and uploads them.</li>
            <li>Open an album and <strong>add more pages</strong> yourself if you have them.</li>
          </ul>
        </section>

        <section className="stack">
          <h2>7. The family tree</h2>
          <ul>
            <li>The <Link to="/tree">tree</Link> opens around you (or the founder). The blue <strong>＋</strong> badge on a card reveals more relatives, <strong>⊕</strong> recenters on that person.</li>
            <li><strong>Find a person</strong>: type a name in the box next to "Expand all". Pick the match and the tree jumps to them, ringed and zoomed in.</li>
            <li>Tap any card to open that person's page: photos they appear in, relatives, and their story.</li>
            <li>Tap <strong>Expand all</strong> to see the whole tree at once. Warning: it can keep you busy for an evening.</li>
          </ul>
          <Shot src="sep5-tree.png" alt="The tree zoomed in on a person found with the search box" />
        </section>

        <section className="stack">
          <h2>8. World map, photos, news</h2>
          <ul>
            <li>The <Link to="/world">World</Link> map shows where everyone lives. Tap a pin for the person's card and a jump to them in the tree.</li>
            <li><Link to="/gallery">Photos &amp; Videos</Link> keeps everything in one grid.</li>
            <li><Link to="/news">News</Link> has the announcements, by category.</li>
          </ul>
        </section>

        <section className="stack">
          <h2>9. Ask the family AI</h2>
          <ul>
            <li>Confused how two people are related? Tap <strong>AI</strong> in the bottom bar and ask. It answers from the tree.</li>
            <li>It can also point you to the right place in the app.</li>
          </ul>
        </section>

        <section className="stack">
          <h2>10. Notifications</h2>
          <ul>
            <li>Turn them on from <Link to="/profile">My profile</Link> → "Notifications on this device", or from the bottom of the menu. Set per device.</li>
            <li>On an iPhone this only works from the home-screen app, not from Safari.</li>
            <li>You'll get a notification and a badge on the app icon when something happens that concerns you.</li>
          </ul>
          <Shot src="sep6-notifications.png" alt="The notifications switch on My profile" />
        </section>

        <section className="stack">
          <h2>11. Something wrong, or an idea?</h2>
          <ul>
            <li>Open <Link to="/issues">Feedback</Link> from the menu, describe it, pick a category, attach a screenshot. Everyone can see what's been reported and its status.</li>
          </ul>
        </section>

        <p className="text-subtle" style={{ fontSize: "0.9rem" }}>
          The site keeps growing, little by little. Sign in, put up your photo,
          and share one memory. Just one, to start.
        </p>
      </main>
    </>
  );
}
